import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FaShoppingBag, FaCreditCard, FaFileInvoice, FaBox, 
  FaTruck, FaCheckCircle, FaClock, FaEye, FaExternalLinkAlt 
} from 'react-icons/fa';
import { toast } from 'sonner';

export default function MyOrders() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders?user_id=${user.id}`);
      // Sort by date descending
      const sortedOrders = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(sortedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status, paymentStatus) => {
    // If pending payment, show payment required
    if (status === 'pending_payment' || paymentStatus === 'pending') {
      return {
        label: i18n.language === 'fr' ? '💳 Paiement requis' : '💳 Payment required',
        className: 'bg-amber-100 text-amber-700 border-amber-300',
        icon: FaCreditCard
      };
    }
    
    const configs = {
      pending: {
        label: i18n.language === 'fr' ? 'En attente' : 'Pending',
        className: 'bg-orange-100 text-orange-700',
        icon: FaClock
      },
      confirmed: {
        label: i18n.language === 'fr' ? 'Confirmée' : 'Confirmed',
        className: 'bg-blue-100 text-blue-700',
        icon: FaCheckCircle
      },
      processing: {
        label: i18n.language === 'fr' ? 'En préparation' : 'Processing',
        className: 'bg-purple-100 text-purple-700',
        icon: FaBox
      },
      shipped: {
        label: i18n.language === 'fr' ? 'Expédiée' : 'Shipped',
        className: 'bg-cyan-100 text-cyan-700',
        icon: FaTruck
      },
      delivered: {
        label: i18n.language === 'fr' ? 'Livrée' : 'Delivered',
        className: 'bg-green-100 text-green-700',
        icon: FaCheckCircle
      },
      completed: {
        label: i18n.language === 'fr' ? 'Terminée' : 'Completed',
        className: 'bg-[#635BFF]/10 text-[#635BFF]',
        icon: FaCheckCircle
      },
      cancelled: {
        label: i18n.language === 'fr' ? 'Annulée' : 'Cancelled',
        className: 'bg-red-100 text-red-700',
        icon: FaClock
      }
    };
    
    return configs[status] || configs.pending;
  };

  const handlePayment = (order) => {
    // Navigate to checkout/payment page with order details
    navigate(`/checkout?order_id=${order.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540]">
            {i18n.language === 'fr' ? '🛒 Mes Commandes' : '🛒 My Orders'}
          </h1>
          <Badge variant="outline" className="text-lg px-4 py-2">
            {orders.length} {i18n.language === 'fr' ? 'commande(s)' : 'order(s)'}
          </Badge>
        </div>

        {orders.length === 0 ? (
          <Card className="p-12 text-center">
            <FaShoppingBag className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-4">
              {i18n.language === 'fr' 
                ? 'Aucune commande pour le moment' 
                : 'No orders yet'}
            </p>
            <p className="text-gray-400 text-sm mb-6">
              {i18n.language === 'fr' 
                ? 'Vos commandes apparaîtront ici une fois que vous aurez validé un devis.' 
                : 'Your orders will appear here once you accept a quote.'}
            </p>
            <Link to="/user/proposals">
              <Button className="bg-[#635BFF]">
                {i18n.language === 'fr' ? 'Voir mes devis' : 'View my quotes'}
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map(order => {
              const statusConfig = getStatusConfig(order.status, order.payment_status);
              const StatusIcon = statusConfig.icon;
              const needsPayment = order.status === 'pending_payment' || order.payment_status === 'pending';
              
              return (
                <Card key={order.id} className={`hover:shadow-lg transition-shadow ${needsPayment ? 'ring-2 ring-amber-400' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                      {/* Left: Order info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-bold text-lg text-[#0A2540]">
                            {i18n.language === 'fr' ? 'Commande' : 'Order'} #{order.id.substring(0, 8).toUpperCase()}
                          </h3>
                          <Badge className={statusConfig.className}>
                            <StatusIcon className="mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-3">
                          {new Date(order.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        
                        {/* Items */}
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center py-1">
                              <div>
                                <span className="font-medium">{item.product_name || item.name || 'Produit'}</span>
                                <span className="text-gray-500 text-sm ml-2">x{item.quantity || 1}</span>
                                {item.type && (
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    {item.type === 'new' ? 'Neuf' : item.type === 'used' ? 'Occasion' : 'Location'}
                                  </Badge>
                                )}
                              </div>
                              <span className="font-medium">{item.total_price || item.unit_price || 0} €</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Quote reference */}
                        {order.quote_id && (
                          <Link to={`/user/proposals/${order.quote_id}`} className="text-sm text-[#635BFF] hover:underline flex items-center gap-1">
                            <FaFileInvoice />
                            {i18n.language === 'fr' ? 'Voir le devis associé' : 'View associated quote'}
                            <FaExternalLinkAlt className="text-xs" />
                          </Link>
                        )}
                      </div>
                      
                      {/* Right: Total and actions */}
                      <div className="text-right md:min-w-[200px]">
                        <p className="text-sm text-gray-500 mb-1">
                          {i18n.language === 'fr' ? 'Total' : 'Total'}
                        </p>
                        <p className="text-3xl font-bold text-[#0A2540] mb-4">
                          {order.total?.toLocaleString('fr-FR')} €
                        </p>
                        
                        {/* Payment button if needed */}
                        {needsPayment ? (
                          <div className="space-y-2">
                            <Button 
                              onClick={() => handlePayment(order)}
                              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                            >
                              <FaCreditCard className="mr-2" />
                              {i18n.language === 'fr' ? 'Procéder au paiement' : 'Proceed to payment'}
                            </Button>
                            <p className="text-xs text-amber-600">
                              {i18n.language === 'fr' 
                                ? '⚠️ Paiement en attente' 
                                : '⚠️ Payment pending'}
                            </p>
                          </div>
                        ) : (
                          <Link to={`/user/orders/${order.id}`}>
                            <Button variant="outline" className="w-full">
                              <FaEye className="mr-2" />
                              {i18n.language === 'fr' ? 'Voir les détails' : 'View details'}
                            </Button>
                          </Link>
                        )}
                        
                        {/* Tracking info if shipped */}
                        {order.tracking_number && (
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <p className="text-xs text-purple-600 font-medium mb-1">
                              <FaTruck className="inline mr-1" />
                              {i18n.language === 'fr' ? 'Numéro de suivi' : 'Tracking'}
                            </p>
                            <p className="font-mono text-sm text-purple-800">{order.tracking_number}</p>
                            {order.carrier && (
                              <p className="text-xs text-gray-500 mt-1">{order.carrier}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
